import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Star, Quotes } from '@phosphor-icons/react';

const testimonials = [
  {
    id: 1,
    text: "Excellent professionnel. Il regarde avant de parler, vous propose la solution la plus efficiente, ne vous pousse pas à la consommation. Réalise toujours un travail de grande qualité, soigne ses finitions et la propreté de ses chantiers.",
    author: "C139 (pages jaunes)",
    highlights: ["Qualité de travail", "Finitions soignées", "Chantiers propres"],
    rating: 5,
  },
  {
    id: 2,
    text: "Très bon professionnel de bonne présentation, rapide et sérieux. Chantier très propre après intervention. De bons conseils et ne pousse pas à la consommation. Devis effectué immédiatement grâce à sa tablette.",
    author: "Delphine01 (pages jaunes)",
    highlights: ["Ponctualité", "Devis rapide", "Sérieux"],
    rating: 5,
  },
  {
    id: 3,
    text: "Travail parfait ! Homme de confiance et de très bon conseils ! Travaux de rénovation dans notre petite ferme en pisé. A toujours été là depuis le devis jusqu'à la fin des travaux. Je recommande les yeux fermés !",
    author: "Xavier",
    highlights: ["Homme de confiance", "Bons conseils"],
    rating: 5,
  },
  {
    id: 4,
    text: "Travaux réalisés avec soins et professionnalisme, compétences, gentillesse, disponibilité. Travail propre et méticuleux de très bon conseil. Félicitations sur tous les plans. Je recommande vivement.",
    author: "A LO",
    highlights: ["Professionnalisme", "Méticuleux"],
    rating: 5,
  },
  {
    id: 5,
    text: "Très bon professionnel. Très belle rénovation de nos locaux. Je recommande. Un vrai pro !",
    author: "ACS",
    highlights: ["Rénovation réussie"],
    rating: 5,
  },
  {
    id: 6,
    text: "Est intervenu en un temps record. Très agréable en plus, nous recommandons vivement !!",
    author: "Anthony",
    highlights: ["Rapidité", "Sympathique"],
    rating: 5,
  },
];

const TestimonialCard = ({ testimonial, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="testimonial-card relative rounded-2xl p-6 md:p-8 border border-black/5 shadow-sm hover:shadow-md transition-shadow duration-300"
      data-testid={`testimonial-card-${testimonial.id}`}
    >
      {/* Quote icon */}
      <Quotes weight="fill" className="w-8 h-8 text-red-100 mb-4" />

      {/* Stars */}
      <div className="flex items-center gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} weight="fill" className="w-4 h-4 star-gold" />
        ))}
      </div>

      {/* Text */}
      <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed mb-4">
        "{testimonial.text}"
      </p>

      {/* Highlights */}
      <div className="flex flex-wrap gap-2 mb-4">
        {testimonial.highlights.map((highlight) => (
          <span
            key={highlight}
            className="px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-[#D32F2F]"
          >
            {highlight}
          </span>
        ))}
      </div>

      {/* Author */}
      <p className="text-sm font-semibold text-[#0A0A0A]">
        – {testimonial.author}
      </p>
    </motion.div>
  );
};

export const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="temoignages" 
      className="py-16 sm:py-24 md:py-32 bg-white"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div ref={ref} className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#D32F2F]"
          >
            Témoignages
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mt-4"
            data-testid="testimonials-title"
          >
            Ce que disent
            <br />
            <span className="text-[#D32F2F]">nos clients</span>
          </motion.h2>

          {/* Google Rating */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center gap-4 mt-6"
            data-testid="google-rating"
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} weight="fill" className={`w-6 h-6 ${i < 5 ? 'star-gold' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-2xl font-bold text-[#0A0A0A]">4,7</span>
            </div>
            <div className="h-8 w-px bg-black/10" />
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm text-[#4A4A4A]">14 avis Google</span>
            </div>
          </motion.div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={testimonial.id} 
              testimonial={testimonial} 
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
